import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  APIProvider,
  Map,
  useMap,
  type MapCameraChangedEvent,
} from '@vis.gl/react-google-maps';
import { useLocationStore } from '@/features/location/store/location-store';
import { useGoogleMapsStore } from '../store/google-maps-store';
import { useChargePoints } from '@/features/charge-points/hooks/useChargePoints';
import { useLocationInformation } from '@/features/location/hooks/useLocationInformation';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { useTheme } from '@/features/theme/hooks/useTheme';
import { ClusterTuningPanel } from '@/features/markers/components/ClusterTuningPanel';
import { ChargePointMarkers } from '@/features/markers/components/ChargePointMarkers';
import { CenterLocationMarker } from './CenterLocationMarker';
import { useKeepSelectionInView } from '../hooks/useKeepSelectionInView';
import { CountryOverlay } from '@/features/country-overlay/components/CountryOverlay';
import { ChargeSessionMarkers } from '@/features/charge-sessions/components/ChargeSessionMarkers';
import { useChargeSessionsStore } from '@/features/charge-sessions/store/charge-sessions-store';
import type { SessionCluster } from '@/features/charge-sessions/types/charge-session.types';
import { MARKER_MIN_ZOOM } from '@/utils/constants';
import { env } from '@/utils/env';

/** Zoom levels to add to the current zoom when a cluster is clicked (expansion). */
const CLUSTER_EXPAND_ZOOM_STEP = 3;
/** Hard ceiling for cluster-expansion zoom (matches the Map's maxZoom). */
const MAX_MAP_ZOOM = 17;

/**
 * Map ID definitions for Cloud Console-configured styles
 */
const MAP_IDS = {
  default: 'monta-web-map',
  silver: 'fd207fbccb63b72190581d26', //'monta-web-map-silver',
};

/**
 * Charge-points data layer: charge-point markers + reverse-geocode anchor.
 *
 * Isolated into its own component so the fetching hooks (`useChargePoints`,
 * `useLocationInformation`) ONLY run in charge-points mode. In charge-sessions
 * mode this subtree is never mounted, guaranteeing zero network I/O (a stray
 * 401 there would silently re-mint a guest token).
 */
function ChargePointsLayer({ isLocked }: { isLocked: boolean }) {
  useKeepSelectionInView();
  const zoomLevel = useLocationStore((s) => s.zoomLevel);
  const { data: chargePointData, error } = useChargePoints();
  const { data: locationInfo } = useLocationInformation();
  const showAnchorPoint = useFilterStore((s) => s.showAnchorPoint);
  const anchorPointVisible = useFilterStore((s) => s.anchorPointVisible);

  // Log errors if charge points fail to load
  useEffect(() => {
    if (error) {
      console.error('[Map] Failed to load charge points:', error);
    }
  }, [error]);

  return (
    <>
      {/* Error indicator */}
      {error && <ChargePointsLoadError />}

      {zoomLevel >= MARKER_MIN_ZOOM && (
        <ChargePointMarkers
          chargePoints={chargePointData?.single_list || []}
          clusters={chargePointData?.cluster_list || []}
          isLocked={isLocked}
        />
      )}

      <CountryOverlay />

      {/* Center location marker - shown only when the anchor_point feature is
          enabled AND the user has toggled the pin on */}
      <CenterLocationMarker
        locationInfo={locationInfo}
        visible={showAnchorPoint && anchorPointVisible}
      />
    </>
  );
}

function ChargePointsLoadError() {
  const { t } = useTranslation();
  return (
    <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20 pointer-events-auto">
      <div className="bg-monta-red text-white shadow-lg px-6 py-4 rounded-lg flex items-center gap-3 max-w-md">
        <svg
          className="w-6 h-6 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-sm">{t('map.loadError')}</span>
          <span className="text-xs opacity-90">
            {t('map.loadErrorDetails')}
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * Charge-sessions data layer: server-clustered live session markers
 * (BIZOPS-1588, contract v2 — viewport clustering).
 *
 * Clusters + leaves are fed solely from the store, which `AppInitializer`'s
 * postMessage bridge fills for whatever viewport the map last reported — this
 * subtree performs ZERO network I/O and never touches the charge-point fetch
 * path. Clicking a cluster zooms the map in (which triggers a fresh
 * `monta:viewport` and finer clusters/leaves); clicking a leaf opens detail.
 */
function ChargeSessionsLayer({ isLocked }: { isLocked: boolean }) {
  const map = useMap();
  const clusters = useChargeSessionsStore((s) => s.clusters);
  const leaves = useChargeSessionsStore((s) => s.leaves);

  const handleClusterClick = useCallback(
    (cluster: SessionCluster) => {
      if (!map) {
        return;
      }
      const currentZoom = map.getZoom() ?? MARKER_MIN_ZOOM;
      const nextZoom = Math.min(
        currentZoom + CLUSTER_EXPAND_ZOOM_STEP,
        MAX_MAP_ZOOM
      );
      map.panTo({ lat: cluster.lat, lng: cluster.lng });
      map.setZoom(nextZoom);
    },
    [map]
  );

  return (
    <ChargeSessionMarkers
      clusters={clusters}
      leaves={leaves}
      onClusterClick={handleClusterClick}
      isLocked={isLocked}
    />
  );
}

/**
 * Inner map component that has access to the map instance
 */
function MapContent() {
  const [searchParams] = useSearchParams();
  const map = useMap();
  const setMapInstance = useGoogleMapsStore((s) => s.setMapInstance);
  const setMapCenter = useLocationStore((s) => s.setMapCenter);
  const setZoomLevel = useLocationStore((s) => s.setZoomLevel);
  const setVisibleRegion = useLocationStore((s) => s.setVisibleRegion);
  const mapCenter = useLocationStore((s) => s.mapCenter);
  const zoomLevel = useLocationStore((s) => s.zoomLevel);
  const shouldInterceptPointer = useGoogleMapsStore(
    (s) => s.shouldInterceptPointer
  );
  // Sessions mode: the postMessage bridge registers a debounced viewport
  // reporter here; we feed it the map bounds/zoom so the parent re-queries the
  // gateway for the visible area (contract v2). `null` until the bridge mounts.
  const sendViewport = useChargeSessionsStore((s) => s.sendViewport);

  // Check if map should be locked (no zoom/pan)
  const isLocked = searchParams.get('locked') === 'true';

  // Charge-sessions mode renders live session markers fed only by postMessage
  // and MUST suppress the charge-point fetch path entirely.
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';

  // Map ID (styles configured in Cloud Console) follows the theme: light uses
  // the colourful default style, dark renders silver with colorScheme DARK.
  // An explicit ?map_style=default|silver still wins.
  const theme = useTheme();
  const mapStyleParam = searchParams.get('map_style');
  const mapStyle =
    mapStyleParam === 'default' || mapStyleParam === 'silver'
      ? mapStyleParam
      : theme === 'dark'
        ? 'silver'
        : 'default';
  const mapId = MAP_IDS[mapStyle];
  const colorScheme = theme === 'dark' ? 'DARK' : 'LIGHT';

  // Set map instance when available
  useEffect(() => {
    if (map) {
      setMapInstance(map);
    }
  }, [map, setMapInstance]);

  // Handle center changed
  const handleCenterChanged = useCallback(
    (event: { detail: { center: google.maps.LatLngLiteral } }) => {
      setMapCenter({
        lat: event.detail.center.lat,
        lng: event.detail.center.lng,
      });
    },
    [setMapCenter]
  );

  // Handle zoom changed
  const handleZoomChanged = useCallback(
    (event: { detail: { zoom: number } }) => {
      setZoomLevel(event.detail.zoom);
    },
    [setZoomLevel]
  );

  // Handle bounds changed
  const handleBoundsChanged = useCallback(
    (event: MapCameraChangedEvent) => {
      const bounds = event.detail.bounds;

      setVisibleRegion({
        north: bounds.north,
        south: bounds.south,
        east: bounds.east,
        west: bounds.west,
      });

      // Sessions mode: report the new viewport to the parent (debounced inside
      // the bridge). Sending on every bounds change + the bridge's debounce
      // gives idle-like semantics (one post once the map settles).
      if (isChargeSessionsMode && sendViewport) {
        sendViewport({
          top: bounds.north,
          bottom: bounds.south,
          left: bounds.west,
          right: bounds.east,
          zoom: event.detail.zoom,
        });
      }
    },
    [setVisibleRegion, isChargeSessionsMode, sendViewport]
  );

  return (
    <Map
      defaultCenter={mapCenter}
      defaultZoom={zoomLevel}
      minZoom={5}
      maxZoom={17}
      gestureHandling={isLocked || shouldInterceptPointer ? 'none' : 'greedy'}
      disableDefaultUI={true}
      // Map styles are configured in Google Cloud Console for each mapId.
      // colorScheme is init-only, so the key remounts the map on theme change
      // (camera is restored from the location store via defaultCenter/Zoom).
      key={`${mapId}-${colorScheme}`}
      mapId={mapId}
      colorScheme={colorScheme}
      onCenterChanged={handleCenterChanged}
      onZoomChanged={handleZoomChanged}
      onBoundsChanged={handleBoundsChanged}
      className="w-full h-full"
      // Disable all interactions when locked
      scrollwheel={!isLocked}
      disableDoubleClickZoom={isLocked}
      draggable={!isLocked}
      clickableIcons={!isLocked}
      keyboardShortcuts={!isLocked}
    >
      {isChargeSessionsMode ? (
        <ChargeSessionsLayer isLocked={isLocked} />
      ) : (
        <ChargePointsLayer isLocked={isLocked} />
      )}
    </Map>
  );
}

/**
 * Google Map View component
 */
export function GoogleMapView() {
  const { t } = useTranslation();

  if (!env.GOOGLE_MAPS_API_KEY) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-monta-gray-100">
        <div className="text-center p-8">
          <p className="text-monta-red font-semibold mb-2">
            {t('map.apiKeyMissing')}
          </p>
          <p className="text-sm text-monta-gray-600">
            {t('map.apiKeyInstruction')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="google-map" className="w-full h-full">
      <APIProvider
        apiKey={env.GOOGLE_MAPS_API_KEY}
        libraries={['places'] as const}
      >
        <MapContent />
      </APIProvider>
      {/* Opt-in cluster tuning for the team: ?cluster_tune=1 */}
      {new URLSearchParams(window.location.search).get('cluster_tune') ===
        '1' && <ClusterTuningPanel />}
    </div>
  );
}
